import React, { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

import Context from "shared/util/Context";
import useTimeout from "shared/hooks/useTimeout";

import {
	MOUSEOVER_TRIGGER,
	OPEN_DELAY,
	LOAD_DELAY,
} from "shared/components/ProfileQuickview/util/constants";

import {
	getPopoverPosition,
} from "shared/components/ProfileQuickview/util/helpers";

import useLoadUser from "shared/components/ProfileQuickview/hooks/useLoadUser";
import ProfileQuickview from "shared/components/ProfileQuickview/ProfileQuickview";

const ProfileQuickviewManager = () => {
	const [show, setShow] = useState(false);
	const [userId, setUserId] = useState(null);
	const [position, setPosition] = useState({});
	const [triggerLoadUser, setTriggerLoadUser] = useState(false);
	const [userData, setUserData] = useState(null);
	const [error, setError] = useState(null);
	const location = useLocation();

	// after mouseenter, wait before showing quickview
	const openTimeoutCallback = useCallback(() => setShow(true), []);
	const openTimeout = useTimeout({
		delay: OPEN_DELAY,
		callback: openTimeoutCallback,
	});

	// after mouseenter, wait before hitting the api for user info
	const loadTimeoutCallback = useCallback(() => setTriggerLoadUser(true), []);
	const loadTimeout = useTimeout({
		delay: LOAD_DELAY,
		callback: loadTimeoutCallback,
	});

	// watch for quickview opening, load user info from the api, and set the result
	useLoadUser({
		triggerLoadUser,
		setTriggerLoadUser,
		userId,
		setUserData,
		setError,
	});

	// reset state on close
	const closePopover = useCallback(() => {
		openTimeout.stop();
		loadTimeout.stop();
		setShow(false);
		setError(null);
		setUserData(null);
		setPosition({});
	}, [openTimeout, loadTimeout]);

	// an event handler for mouseenter
	const onEnter = useCallback((e) => {
		const targetUserId = e?.target?.getAttribute(MOUSEOVER_TRIGGER);

		// short circuit if there's no target, or you are trying to open your own
		if (!targetUserId || targetUserId === Context.get("userid")) {
			return;
		}

		openTimeout.stop();
		setUserId(targetUserId);
		setPosition(getPopoverPosition(e.target));
		openTimeout.start();
		loadTimeout.start();
	}, [openTimeout, loadTimeout]);

	// an event handler for mouseleave
	const onLeave = useCallback((e) => {
		if (!e?.target?.getAttribute(MOUSEOVER_TRIGGER)) {
			return;
		}

		closePopover();
	}, [closePopover]);

	// close the popover on page transitions
	useEffect(() => {
		process.nextTick(() => {
			closePopover();
		});
	}, [location.pathname, closePopover]);

	useEffect(() => {
		document.body.addEventListener("mouseenter", onEnter, true);
		document.body.addEventListener("mouseleave", onLeave, true);

		return () => {
			document.body.removeEventListener("mouseenter", onEnter, true);
			document.body.removeEventListener("mouseleave", onLeave, true);
			openTimeout.stop();
			loadTimeout.stop();
		};
	}, [onEnter, onLeave, openTimeout, loadTimeout]);

	return (
		<ProfileQuickview
			show={show}
			position={position}
			userData={userData}
			error={error}
		/>
	);
};

export default ProfileQuickviewManager;



// WEBPACK FOOTER //
// ./src/shared/components/ProfileQuickview/Manager.jsx